/* istanbul ignore file */
/* tslint:disable */
 
import type { AccidentContractType } from '../models/AccidentContractType';
import type { AccidentDuration } from '../models/AccidentDuration';
import type { AccidentProfession } from '../models/AccidentProfession';
import type { AccidentRisk } from '../models/AccidentRisk';
import type { AccidentSport } from '../models/AccidentSport';
import type { AccidentTerritory } from '../models/AccidentTerritory';
import type { PaginatedAccidentsBorderoList } from '../models/PaginatedAccidentsBorderoList';
import type { PatchedAccidentContractType } from '../models/PatchedAccidentContractType';
import type { PatchedAccidentDuration } from '../models/PatchedAccidentDuration';
import type { PatchedAccidentProfession } from '../models/PatchedAccidentProfession';
import type { PatchedAccidentRisk } from '../models/PatchedAccidentRisk';
import type { PatchedAccidentSport } from '../models/PatchedAccidentSport';
import type { PatchedAccidentTerritory } from '../models/PatchedAccidentTerritory';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class AccidentsService {

  /**
   * Bordero export for Accident insurance.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns PaginatedAccidentsBorderoList 
   * @throws ApiError
   */
  public static accidentsBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedAccidentsBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Bordero export for Accident insurance.
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
   * @param contractType * `order` - Order
 * * `modification` - Modification
 * * `cancel` - Cancel
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search Search fields: number, order__contractor__as_person__first_name, order__contractor__as_person__last_name, order__contractor__as_legalentity__name, order__contractor__as_legalentity__identification_number, order__contractor__as_person__identification_number
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static accidentsBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee',
contractType?: 'cancel' | 'modification' | 'order',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'contract_type': contractType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * @param ordering Ordering fields: code, persons
   * @returns AccidentContractType 
   * @throws ApiError
   */
  public static accidentsContractTypesList(
ordering?: string,
): CancelablePromise<Array<AccidentContractType>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/contract-types/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this accident contract.
   * @returns AccidentContractType 
   * @throws ApiError
   */
  public static accidentsContractTypesRetrieve(
id: number,
): CancelablePromise<AccidentContractType> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/contract-types/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this accident contract.
   * @param requestBody 
   * @returns AccidentContractType 
   * @throws ApiError
   */
  public static accidentsContractTypesPartialUpdate(
id: number,
requestBody?: PatchedAccidentContractType,
): CancelablePromise<AccidentContractType> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/contract-types/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: months
   * @returns AccidentDuration 
   * @throws ApiError
   */
  public static accidentsDurationsList(
ordering?: string,
): CancelablePromise<Array<AccidentDuration>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/durations/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param months A unique value identifying this accident duration.
   * @returns AccidentDuration 
   * @throws ApiError
   */
  public static accidentsDurationsRetrieve(
months: number,
): CancelablePromise<AccidentDuration> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/durations/{months}/',
      path: {
        'months': months,
      },
    });
  }

  /**
   * @param months A unique value identifying this accident duration.
   * @param requestBody 
   * @returns AccidentDuration 
   * @throws ApiError
   */
  public static accidentsDurationsPartialUpdate(
months: number,
requestBody?: PatchedAccidentDuration,
): CancelablePromise<AccidentDuration> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/durations/{months}/',
      path: {
        'months': months,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: code, coefficient
   * @returns AccidentProfession 
   * @throws ApiError
   */
  public static accidentsProfessionsList(
ordering?: string,
): CancelablePromise<Array<AccidentProfession>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/professions/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param code 
   * @returns AccidentProfession 
   * @throws ApiError
   */
  public static accidentsProfessionsRetrieve(
code: 'low_risk' | 'medium_risk' | 'high_risk' | 'special_risk',
): CancelablePromise<AccidentProfession> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/professions/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param code 
   * @param requestBody 
   * @returns AccidentProfession 
   * @throws ApiError
   */
  public static accidentsProfessionsPartialUpdate(
code: 'low_risk' | 'medium_risk' | 'high_risk' | 'special_risk',
requestBody?: PatchedAccidentProfession,
): CancelablePromise<AccidentProfession> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/professions/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: name, coefficient
   * @returns AccidentRisk 
   * @throws ApiError
   */
  public static accidentsRisksList(
ordering?: string,
): CancelablePromise<Array<AccidentRisk>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/risks/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param code 
   * @returns AccidentRisk 
   * @throws ApiError
   */
  public static accidentsRisksRetrieve(
code: 'invalidity' | 'death' | 'traumatism' | 'outpatient_daily_allowance' | 'inpatient_daily_allowance' | 'all_main_risks' | 'sudden_death_financial_loss' | 'repatriation_financial_loss' | 'passenger_travel_accident' | 'driver_accident',
): CancelablePromise<AccidentRisk> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/risks/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param code 
   * @param requestBody 
   * @returns AccidentRisk 
   * @throws ApiError
   */
  public static accidentsRisksPartialUpdate(
code: 'invalidity' | 'death' | 'traumatism' | 'outpatient_daily_allowance' | 'inpatient_daily_allowance' | 'all_main_risks' | 'sudden_death_financial_loss' | 'repatriation_financial_loss' | 'passenger_travel_accident' | 'driver_accident',
requestBody?: PatchedAccidentRisk,
): CancelablePromise<AccidentRisk> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/risks/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: code, coefficient
   * @returns AccidentSport 
   * @throws ApiError
   */
  public static accidentsSportsList(
ordering?: string,
): CancelablePromise<Array<AccidentSport>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/sports/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param code 
   * @returns AccidentSport 
   * @throws ApiError
   */
  public static accidentsSportsRetrieve(
code: 'no_risk' | 'medium_risk' | 'high_risk',
): CancelablePromise<AccidentSport> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/sports/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param code 
   * @param requestBody 
   * @returns AccidentSport 
   * @throws ApiError
   */
  public static accidentsSportsPartialUpdate(
code: 'no_risk' | 'medium_risk' | 'high_risk',
requestBody?: PatchedAccidentSport,
): CancelablePromise<AccidentSport> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/sports/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param ordering Ordering fields: code, coefficient
   * @returns AccidentTerritory 
   * @throws ApiError
   */
  public static accidentsTerritoriesList(
ordering?: string,
): CancelablePromise<Array<AccidentTerritory>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/territories/',
      query: {
        'ordering': ordering,
      },
    });
  }

  /**
   * @param code 
   * @returns AccidentTerritory 
   * @throws ApiError
   */
  public static accidentsTerritoriesRetrieve(
code: 'national' | 'international',
): CancelablePromise<AccidentTerritory> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/accidents/territories/{code}/',
      path: {
        'code': code,
      },
    });
  }

  /**
   * @param code 
   * @param requestBody 
   * @returns AccidentTerritory 
   * @throws ApiError
   */
  public static accidentsTerritoriesPartialUpdate(
code: 'national' | 'international',
requestBody?: PatchedAccidentTerritory,
): CancelablePromise<AccidentTerritory> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/accidents/territories/{code}/',
      path: {
        'code': code,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

}
